import React, { useState } from "react";
import ReactDOM from "react-dom";
import EventListener from "react-event-listener";
import { Assert, hv, ifNodeEmpty } from "Tools";
import { BulmaButton } from "./Button";
import { useElement } from "./Hooks/Hooks";

interface ModalProps {
  children?: React.ReactNode;
  isVisible: boolean;
  title: React.ReactNode;
  buttonRow?: React.ReactNode;
  onCancel: () => void;
  zOffset?: number;
  theme?: { section?: React.CSSProperties };
}

function ModalInner(props: ModalProps) {
  if (!props.isVisible) {
    return null;
  }
  const handleKeyDown = (e: any) => {
    const escapeKeyCode = 27;
    switch (e.keyCode) {
      case escapeKeyCode:
        props.onCancel();
        break;
      default:
        break;
    }
  };
  return (
    <div
      className="modal is-active"
      style={{ zIndex: 40 + (props.zOffset ?? 0) }}
    >
      <EventListener target={document} onKeyDownCapture={handleKeyDown} />
      <div className="modal-background" />
      <div className="modal-card">
        <header style={{ overflow: "visible" }} className="modal-card-head">
          <div style={{ maxWidth: "95%" }} className="modal-card-title">
            {props.title}
          </div>
          <div className="button-wrapper">
            <div
              className="click-catcher"
              onClick={props.onCancel}
              style={{ cursor: "pointer" }}
            />
            <button
              type="button"
              className="delete is-medium"
              onClick={props.onCancel}
            />
          </div>
        </header>
        <section className="modal-card-body" style={props.theme?.section}>
          <div className="content">{props.children}</div>
        </section>
        <footer
          className="modal-card-foot"
          style={{ justifyContent: "center" }}
        >
          {props.buttonRow}
        </footer>
      </div>
    </div>
  );
}

export function Modal(props: ModalProps) {
  const modalRootElement = document.getElementById("react-modal-root");

  Assert.hard(hv(modalRootElement), "Cannot find root for Modal component");

  const target = useElement(modalRootElement, () =>
    document.createElement("div"),
  );

  return ReactDOM.createPortal(<ModalInner {...props} />, target);
}

export function ControlWithModal(
  props: Omit<
    ModalProps,
    "isVisible" | "footer" | "onEnterPressed" | "onCancel"
  > & {
    control: (openModal: () => void) => React.ReactNode;
    onSubmit: () => void;
    cancelButtonContent?: React.ReactNode;
    submitButtonContent?: React.ReactNode;
    isSubmitting?: boolean;
  },
) {
  const [isVisible, setVisible] = useState(false);
  const onSubmit = () => {
    props.onSubmit();
    setVisible(false);
  };
  const onCancel =
    props.isSubmitting === true ? () => null : () => setVisible(false);
  return (
    <>
      <Modal
        isVisible={isVisible}
        buttonRow={
          <>
            <BulmaButton
              className="is-light"
              isSubmitting={props.isSubmitting}
              onClick={onCancel}
            >
              {ifNodeEmpty(props.cancelButtonContent, "Cancel")}
            </BulmaButton>
            <BulmaButton
              className="is-primary"
              isSubmitting={props.isSubmitting}
              onClick={onSubmit}
            >
              {ifNodeEmpty(props.submitButtonContent, "OK")}
            </BulmaButton>
          </>
        }
        onCancel={onCancel}
        {...props}
      >
        {props.children}
      </Modal>
      {props.control(() => setVisible(true))}
    </>
  );
}
